import type { OrganizationRole, SystemRole } from "../auth/auth.types";

/**
 * Represents a member in the organization admin context
 */
export type OrganizationMemberDTO = {
	name: string | null;
	id: string;
	createdAt: string;
	lastActive: string;
	email: string;
	systemRoles: SystemRole[];
	gruppen: UserGruppeSummaryDTO[];
	orgaRoles: OrganizationRole[];
	invitations: OrganizationMemberInvitationDTO[];
};

/**
 * Gruppe with assignment statistics
 * This is used to return the assignment statistics for a gruppe
 */
export type OrgaAdminGruppeWithAssignmentStatistics = OrgaAdminGruppeDTO & {
	korrekturStepsWithZuweisungPercentage: number;
	korrekturStepsCompletedPercentage: number;
};

export type OrganizationMemberInvitationDTO = {
	id: string;
	organizationId: string;
	email: string;
	createdAt: string;
	expiresAt: string;
	acceptedAt: string | null;
	userId: string;
};

/**
 * Gruppe domain types for orga admin
 */
export type GruppeUserDTO = {
	id: string;
	name: string | null;
	email: string;
};

export type GruppeKlausurDTO = {
	id: string;
	title?: string;
};

export type GruppeStudentKlausurDTO = {
	id: string;
	title?: string;
};

export type GruppeDTO = {
	id: string;
	title: string;
	organizationId: string;
	studentKlausuren: GruppeStudentKlausurDTO[];
	users: GruppeUserDTO[];
	klausuren: GruppeKlausurDTO[];
};

export type UserGruppeSummaryDTO = {
	id: string;
	title: string;
	organizationId: string;
};

// this is the detailed student klausur with all korrektur steps
export interface StudentKlausurDetailedDTO {
	id: string;
	internalIdentifier: string;
	originalFileId: string;
	klausurId: string;
	gruppeId: string;
	nextKorrekturStepId: string;
	originalFile: StorageFileDTO;
	korrekturSteps: KorrekturStep[];
}

export interface ReducedKorrekturStudentKlausurDTO {
	id: string;
	lockedTotalPoints: number;
	lockedMaxPoints: number;
}

export interface KorrekturStep {
	id: string;
	createdAt: Date;
	updatedAt: Date;
	status: KorrekturStepStatus;
	type: KorrekturStepType;
	korrektorId: string | null;
	studentKlausurId: string;
	korrekturStudentKlausurId: null;
	stepNr: number;
	korrekturStudentKlausur: ReducedKorrekturStudentKlausurDTO;
	korrekturStudentKlausuren?: ReducedKorrekturStudentKlausurDTO[];
	korrektor?: {
		id: string;
		name: string | null;
		email: string;
	} | null;
}

export enum KorrekturStepStatus {
	LOCKED = "LOCKED",
	NOT_STARTED = "NOT_STARTED",
	IN_PROGRESS = "IN_PROGRESS",
	COMPLETED = "COMPLETED",
}

export enum KorrekturStepType {
	Default = "DEFAULT",
	REVIEW = "REVIEW",
}

export interface StorageFileDTO {
	id: string;
	createdAt: Date;
	updatedAt: Date;
	blobLink: string;
}

// This is the DTO for the gruppe endpoint in the orga admin context
export interface OrgaAdminGruppeDTO {
	id: string;
	title: string;
	organizationId: string;
	users: OrgaAdminUser[];
	studentKlausuren: StudentKlausur[];
	klausuren: GruppeKlausurDTO[];
	organization: Organization;
}

// this is a user which is a member of a gruppe
export interface OrgaAdminUser {
	id: string;
	createdAt: Date;
	lastActive: Date;
	email: string;
	name: string;
	systemRoles: string[];
}

export interface Organization {
	enabled: boolean;
	disabledAt: null;
	isDefault: boolean;
	id: string;
	name: string;
	createdAt: Date;
	description: string;
	slug: string;
	defaultKorrekturSteps: number;
}

export interface StudentKlausur {
	id: string;
	internalIdentifier: string;
	originalFileId: string;
	klausurId: string;
	gruppeId: string;
	nextKorrekturStepId: string;
	korrekturSteps?: KorrekturStep[];
}
